"use client";
import {useRef, useState} from "react";
import {useForm} from "react-hook-form";
import type {FormData} from "@/types/types";
import FormInput from "./FormInput";
import FormTextArea from "./FormTextArea";
import FormSuccessToast, {
  FormSuccessToastRef,
} from "@/components/forms/FormSuccessToast";
import posthog from "posthog-js";

export default function ContactForm() {
  const toastRef = useRef<FormSuccessToastRef>(null);
  const {
    register,
    handleSubmit,
    reset,
    formState: {errors, isSubmitting},
  } = useForm<FormData>();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const onSubmit = async (data: FormData) => {
    setSubmitError(null);
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify(data),
      });

      const result = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(result.error || "Failed to send message.");
      }

      if (result.emailDelivered !== false) {
        posthog.capture("contact_form_submitted", {email: data.email});
        toastRef.current?.show({
          message: "TRANSMISSION_SUCCESSFUL",
          type: "success",
        });
        reset();
      } else {
        posthog.capture("contact_form_partial_submit", {email: data.email});
        toastRef.current?.show({
          message: "MESSAGE_SAVED__NOTIFICATION_DELAYED",
          type: "warning",
        });
        reset();
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : "Something went wrong. Please try again.";
      setSubmitError(errorMessage);
    }
  };

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-6 w-full bg-zinc-900/40 border-2 border-zinc-800 p-8 rounded-none text-left font-mono"
        aria-live="polite"
      >
        <div className="sr-only" aria-hidden="true">
          <label htmlFor="honeypot">Leave this field empty if you are human</label>
          <input
            id="honeypot"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            {...register("honeypot")}
          />
        </div>

        <FormInput
          id="name"
          label="SENDER_NAME"
          type="text"
          placeholder="ENTER NAME..."
          {...register("name", {required: "Name is required"})}
          error={errors.name?.message}
        />

        <FormInput
          id="email"
          label="SENDER_EMAIL"
          type="email"
          placeholder="ENTER EMAIL..."
          {...register("email", {required: "Email is required"})}
          error={errors.email?.message}
        />

        <FormTextArea
          id="message"
          label="MESSAGE_PAYLOAD"
          placeholder="TYPE MESSAGE PACKET..."
          {...register("message", {required: "Message is required"})}
          error={errors.message?.message}
        />

        {submitError && (
          <div className="text-red-400 text-sm font-bold mt-2" role="alert">
            [ERROR] {submitError} <br />
            Fallback: <a href="mailto:joelolajiire@gmail.com" className="underline">joelolajiire@gmail.com</a>
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full py-3 px-6 font-bold border-2 border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-zinc-950 disabled:opacity-50 transition-none cursor-pointer rounded-none uppercase text-xs tracking-wider bg-transparent hover-glitch"
        >
          {isSubmitting ? "TRANSMITTING..." : "SUBMIT_PAYLOAD_PACKET"}
        </button>
      </form>

      <FormSuccessToast ref={toastRef} />
    </>
  );
}
